import { NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { DashboardChannel, DashboardErp, DashboardInvoiceFlow } from '../../models/dashboard.model';
import {
  channelLabel,
  count,
  FlowCell,
  FlowNote,
  FlowQuery,
  flowScope,
  invoiceLink,
  notApplicableCell,
  notApplicableReason,
  note,
  ofChannelsText,
  totalCell,
} from '../../utils/dashboard-flow';
import { DashboardFlowCellComponent } from '../dashboard-flow-cell/dashboard-flow-cell.component';

interface InvoiceFlowLine {
  id: number | null;
  name: string;
  issued: FlowCell;
  authorized: FlowCell;
  toChannel: FlowCell;
  toErp: FlowCell;
  cancelledOrDenied: FlowCell;
  returns: FlowCell;
}

// Valores de um passo por linha: null = o passo não se aplica (pela origem das notas).
interface StepValues {
  id: number;
  main: number | null;
  failed: number;
}

// Fluxo das notas fiscais (spec §9): notas de venda e devolução, de qualquer origem, no período. Emitidas
// (pelo ERP, pelo canal) → Autorizadas (pendentes) → Enviadas ao canal (só nota do ERP) → Enviadas ao ERP
// (só nota do canal) → Canceladas ou denegadas → Devoluções. Cada número abre a lista de notas no passo;
// a lista não filtra pela origem ERP sozinha, então "N pelo ERP" fica sem link (melhor que abrir a lista
// sem o recorte).
@Component({
  selector: 'app-dashboard-invoice-flow',
  standalone: true,
  imports: [NgTemplateOutlet, DashboardFlowCellComponent],
  host: { class: 'col-12' },
  styleUrls: ['../dashboard-table.scss'],
  template: `
    <div class="card h-100">
      <div class="card-header">
        <h5 class="card-title mb-0">Fluxo das notas fiscais</h5>
        <small class="text-muted">Notas de venda e devolução do período</small>
      </div>
      <div class="card-body">
        @if (empty) {
          <div class="text-center text-muted py-4" data-empty>
            <i class="ti ti-file-off d-block mb-2" style="font-size: 1.75rem; opacity: 0.5"></i>
            <p class="mb-3 small">Nenhuma nota fiscal no período.</p>
            <button type="button" class="btn btn-sm btn-label-primary" (click)="widenPeriod.emit()">
              Ver últimos 90 dias
            </button>
          </div>
        } @else {
          <div class="dash-table-wrap">
            <table class="dash-table">
              <thead>
                <tr>
                  <th scope="col">Canal</th>
                  <th scope="col" data-col="issued">Emitidas</th>
                  <th scope="col" data-col="authorized">Autorizadas</th>
                  <th scope="col" data-col="toChannel">Enviadas ao canal</th>
                  <th scope="col" data-col="toErp">Enviadas ao ERP</th>
                  <th scope="col" data-col="cancelledOrDenied">Canceladas ou denegadas</th>
                  <th scope="col" data-col="returns">Devoluções</th>
                </tr>
              </thead>
              <tbody>
                @for (line of lines; track line.id) {
                  <ng-container *ngTemplateOutlet="row; context: { $implicit: line }"></ng-container>
                }
              </tbody>
              @if (total) {
                <tfoot>
                  <ng-container *ngTemplateOutlet="row; context: { $implicit: total }"></ng-container>
                </tfoot>
              }
            </table>
          </div>
        }
      </div>
    </div>

    <ng-template #row let-line>
      <tr [attr.data-row]="line.id ?? 'total'">
        <td><span class="dash-channel-name">{{ line.name }}</span></td>
        <td data-col="issued"><app-dashboard-flow-cell [cell]="line.issued"></app-dashboard-flow-cell></td>
        <td data-col="authorized"><app-dashboard-flow-cell [cell]="line.authorized"></app-dashboard-flow-cell></td>
        <td data-col="toChannel"><app-dashboard-flow-cell [cell]="line.toChannel"></app-dashboard-flow-cell></td>
        <td data-col="toErp"><app-dashboard-flow-cell [cell]="line.toErp"></app-dashboard-flow-cell></td>
        <td data-col="cancelledOrDenied"><app-dashboard-flow-cell [cell]="line.cancelledOrDenied"></app-dashboard-flow-cell></td>
        <td data-col="returns"><app-dashboard-flow-cell [cell]="line.returns"></app-dashboard-flow-cell></td>
      </tr>
    </ng-template>
  `,
})
export class DashboardInvoiceFlowComponent implements OnChanges {
  /** `invoiceFlow` do snapshot. */
  @Input() rows?: DashboardInvoiceFlow[] | null;
  @Input() channels?: DashboardChannel[] | null;
  @Input() erp?: DashboardErp | null;
  @Input() period?: { from?: string; to?: string };
  @Output() widenPeriod = new EventEmitter<void>();

  lines: InvoiceFlowLine[] = [];
  total: InvoiceFlowLine | null = null;
  empty = true;

  ngOnChanges(): void {
    const rows = this.rows ?? [];
    this.empty = rows.reduce((s, r) => s + r.issuedByErp + r.issuedByChannel, 0) === 0;

    const perRow = rows.map((r) => {
      const channel = this.channels?.find((c) => c.integrationId === r.integrationId);
      const toChannelReason = notApplicableReason('invoiceToChannel', channel, this.erp, r);
      const toErpReason = notApplicableReason('invoiceToErp', channel, this.erp, r);
      return {
        row: r,
        toChannelReason,
        toErpReason,
        toChannel: { id: r.integrationId, main: toChannelReason ? null : r.channelUploaded, failed: r.channelUploadFailed } as StepValues,
        toErp: { id: r.integrationId, main: toErpReason ? null : r.erpConfirmed, failed: r.erpFailed } as StepValues,
      };
    });

    this.lines = perRow.map((p): InvoiceFlowLine => {
      const r = p.row;
      const scope = flowScope(this.period, [r.integrationId]);
      return {
        id: r.integrationId,
        name: channelLabel(r.integrationId, this.channels),
        ...this.commonCells(r, scope),
        toChannel: p.toChannelReason ? notApplicableCell(p.toChannelReason) : this.toChannelCell(p.toChannel, scope, null),
        toErp: p.toErpReason ? notApplicableCell(p.toErpReason) : this.toErpCell(p.toErp, scope, null),
      };
    });

    if (rows.length < 2) {
      this.total = null;
      return;
    }

    const sum = (f: (r: DashboardInvoiceFlow) => number) => rows.reduce((s, r) => s + f(r), 0);
    const totalRow: DashboardInvoiceFlow = {
      integrationId: 0,
      issuedByErp: sum((r) => r.issuedByErp),
      issuedByChannel: sum((r) => r.issuedByChannel),
      authorized: sum((r) => r.authorized),
      pending: sum((r) => r.pending),
      channelUploaded: 0,
      channelUploadFailed: 0,
      erpConfirmed: 0,
      erpFailed: 0,
      cancelledOrDenied: sum((r) => r.cancelledOrDenied),
      returns: sum((r) => r.returns),
    };

    const toChannel = this.totalStep(perRow.map((p) => p.toChannel));
    const toErp = this.totalStep(perRow.map((p) => p.toErp));
    this.total = {
      id: null,
      name: 'Total',
      ...this.commonCells(totalRow, flowScope(this.period, null)),
      toChannel: toChannel ? this.toChannelCell(toChannel.values, toChannel.scope, toChannel.of) : notApplicableCell(null),
      toErp: toErp ? this.toErpCell(toErp.values, toErp.scope, toErp.of) : notApplicableCell(null),
    };
  }

  /** Total de um passo: só as linhas em que ele existe; o link leva só esses canais. */
  private totalStep(values: StepValues[]): { values: StepValues; scope: FlowQuery; of: string | null } | null {
    const { value, of } = totalCell(values.map((v) => v.main));
    if (value === null) return null;
    const applicable = values.filter((v) => v.main !== null);
    const partial = of < values.length;
    return {
      values: { id: 0, main: value, failed: applicable.reduce((s, v) => s + v.failed, 0) },
      scope: flowScope(this.period, partial ? applicable.map((v) => v.id) : null),
      of: ofChannelsText(of, values.length),
    };
  }

  private commonCells(
    r: DashboardInvoiceFlow,
    scope: FlowQuery,
  ): Pick<InvoiceFlowLine, 'issued' | 'authorized' | 'cancelledOrDenied' | 'returns'> {
    const origin: FlowNote[] = [];
    // Nota emitida pelo ERP sozinha não tem filtro na lista: o número fica sem link.
    if (r.issuedByErp > 0) origin.push({ text: `${count(r.issuedByErp)} pelo ERP`, link: null, tone: 'muted' });
    if (r.issuedByChannel > 0) {
      origin.push({ text: `${count(r.issuedByChannel)} pelo canal`, link: invoiceLink('issuedByChannel', scope), tone: 'muted' });
    }
    return {
      issued: { value: r.issuedByErp + r.issuedByChannel, link: invoiceLink('issued', scope), notes: origin },
      authorized: {
        value: r.authorized,
        link: invoiceLink('authorized', scope),
        notes: note(r.pending, ['pendente', 'pendentes'], 'warning', invoiceLink('pending', scope)),
      },
      cancelledOrDenied: { value: r.cancelledOrDenied, link: invoiceLink('cancelledOrDenied', scope), notes: [] },
      returns: { value: r.returns, link: invoiceLink('returns', scope), notes: [] },
    };
  }

  private toChannelCell(v: StepValues, scope: FlowQuery, of: string | null): FlowCell {
    return {
      value: v.main,
      link: invoiceLink('channelUploaded', scope),
      of,
      notes: note(v.failed, ['com falha', 'com falha'], 'danger', invoiceLink('channelUploadFailed', scope)),
    };
  }

  private toErpCell(v: StepValues, scope: FlowQuery, of: string | null): FlowCell {
    return {
      value: v.main,
      link: invoiceLink('erpConfirmed', scope),
      of,
      notes: note(v.failed, ['com falha', 'com falha'], 'danger', invoiceLink('erpFailed', scope)),
    };
  }
}
